const pad2 = (n) => String(Math.trunc(Number(n) || 0)).padStart(2, '0');

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toISODate = (date) => {
  if (!date) return null;
  const d = startOfDay(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const parseISODate = (s) => {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const firstOfMonth = (date) => {
  const d = startOfDay(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const firstOfQuarter = (date) => {
  const d = startOfDay(date);
  const q0 = Math.floor(d.getMonth() / 3) * 3;
  return new Date(d.getFullYear(), q0, 1);
};

const firstOfYear = (date) => {
  const d = startOfDay(date);
  return new Date(d.getFullYear(), 0, 1);
};

const bucketStartForInterval = (interval, date) => {
  if (!date) return null;
  const key = String(interval || 'month').toLowerCase();
  if (key === 'year') return firstOfYear(date);
  if (key === 'quarter') return firstOfQuarter(date);
  return firstOfMonth(date);
};

const addMonths = (date, count) => {
  const d = startOfDay(date);
  const next = new Date(d.getFullYear(), d.getMonth() + Number(count || 0), 1);
  next.setHours(0, 0, 0, 0);
  return next;
};

const addYears = (date, count) => {
  const d = startOfDay(date);
  const next = new Date(d.getFullYear() + Number(count || 0), d.getMonth(), d.getDate());
  next.setHours(0, 0, 0, 0);
  return next;
};

const buildPeriodStarts = ({ interval, rangeStart, rangeEnd }) => {
  const start = bucketStartForInterval(interval, rangeStart);
  const end = bucketStartForInterval(interval, rangeEnd);
  if (!start || !end) return [];

  const key = String(interval || 'month').toLowerCase();
  const stepMonths = key === 'year' ? 12 : key === 'quarter' ? 3 : 1;

  const out = [];
  let cur = start;
  while (cur.getTime() <= end.getTime()) {
    out.push(cur);
    cur = addMonths(cur, stepMonths);
  }
  return out;
};

const formatCurrency = (amount) => {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

const formatHours = (hours) => {
  const n = Number(hours) || 0;
  return `${n.toFixed(2)}h`;
};

const formatYearSuffix = (date) => {
  const d = startOfDay(date);
  return `'${String(d.getFullYear()).slice(-2)}`;
};

const formatMonthLabel = (date, includeYear) => {
  const d = startOfDay(date);
  const month = d.toLocaleString('en-US', { month: includeYear ? 'short' : 'long' });
  return includeYear ? `${month} ${formatYearSuffix(d)}` : month;
};

const formatQuarterLabel = (date, includeYear) => {
  const d = startOfDay(date);
  const q = Math.floor(d.getMonth() / 3) + 1;
  return includeYear ? `Q${q} ${formatYearSuffix(d)}` : `Q${q}`;
};

const formatYearLabel = (date) => {
  const d = startOfDay(date);
  return String(d.getFullYear());
};

const formatXAxisTick = (rawLabel, interval, includeYear) => {
  const d = parseISODate(String(rawLabel));
  if (!d) return String(rawLabel);
  const key = String(interval || 'month').toLowerCase();
  if (key === 'year') return formatYearLabel(d);
  if (key === 'quarter') return formatQuarterLabel(d, includeYear);
  return formatMonthLabel(d, includeYear);
};

const formatTooltipTitle = (rawLabel, interval, includeYear) => {
  const d = parseISODate(String(rawLabel));
  if (!d) return String(rawLabel);
  const key = String(interval || 'month').toLowerCase();
  if (key === 'year') return formatYearLabel(d);
  if (key === 'quarter') return formatQuarterLabel(d, includeYear);

  const month = d.toLocaleString('en-US', { month: 'long' });
  return includeYear ? `${month} ${d.getFullYear()}` : month;
};

const readCssVar = (name, fallback = '') => {
  try {
    if (typeof document === 'undefined') return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(name);
    const v = String(value || '').trim();
    return v || fallback;
  } catch {
    return fallback;
  }
};

export {
  pad2,
  toISODate,
  parseISODate,
  startOfDay,
  firstOfMonth,
  firstOfQuarter,
  firstOfYear,
  bucketStartForInterval,
  addMonths,
  addYears,
  buildPeriodStarts,
  formatCurrency,
  formatHours,
  formatYearSuffix,
  formatMonthLabel,
  formatQuarterLabel,
  formatYearLabel,
  formatXAxisTick,
  formatTooltipTitle,
  readCssVar,
};
